import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { MessageCircle, Phone, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const CHANNELS = [
  { id: "sms", label: "SMS", icon: Phone },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
];

export default function PhoneOtpModal({ open, onOpenChange }) {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("sms");
  const [otpHint, setOtpHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const reset = () => {
    setStep("phone");
    setPhone("");
    setCode("");
    setName("");
    setOtpHint("");
    setChannel("sms");
  };

  const sendOtp = async (ch = channel, isResend = false) => {
    if (!phone || phone.length < 10) {
      toast.error("Enter a valid phone number");
      return;
    }
    isResend ? setResending(true) : setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone, channel: ch });
      // Server only returns `otp` in dev-mode (no Pingbix). Show it as a hint.
      setOtpHint(data.otp ? `Dev OTP: ${data.otp}` : "");
      setStep("otp");
      toast.success(
        data.delivered
          ? `OTP sent via ${ch === "whatsapp" ? "WhatsApp" : "SMS"}`
          : "OTP issued (dev mode — shown below)"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
      setResending(false);
    }
  };

  const verify = async () => {
    if (!code || code.length < 4) {
      toast.error("Enter the OTP");
      return;
    }
    setLoading(true);
    try {
      const u = await login(phone, code, name || undefined);
      toast.success(`Welcome, ${u.name || u.phone}`);
      onOpenChange(false);
      reset();
      // Auto-route privileged roles to their console after sign-in.
      if (u.role === "super_admin") navigate("/superadmin");
      else if (u.role === "admin") navigate("/admin");
      else if (u.role === "seller") navigate("/seller");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-md rounded-none border-[hsl(var(--kb-border))]" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight">
            {step === "phone" ? "Sign in" : "Enter OTP"}
          </DialogTitle>
          <DialogDescription className="overline">
            {step === "phone"
              ? "Quick phone login"
              : `Sent to +91 ${phone} via ${channel === "whatsapp" ? "WhatsApp" : "SMS"}`}
          </DialogDescription>
        </DialogHeader>

        {step === "phone" ? (
          <div className="space-y-6 pt-4">
            <input
              data-testid="phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              placeholder="Phone number"
              className="kb-input"
              maxLength={10}
              autoFocus
            />
            <input
              data-testid="name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="kb-input"
            />
            <div>
              <div className="overline mb-2">Send OTP via</div>
              <div className="grid grid-cols-2 gap-2" data-testid="channel-picker">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  const active = channel === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setChannel(c.id)}
                      data-testid={`channel-${c.id}`}
                      className={`flex items-center justify-center gap-2 py-2 text-sm border transition-colors ${
                        active
                          ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]"
                          : "border-[hsl(var(--kb-border))] hover:border-[hsl(var(--kb-primary))]"
                      }`}
                    >
                      <Icon size={14} /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              data-testid="send-otp-btn"
              disabled={loading}
              onClick={() => sendOtp(channel, false)}
              className="kb-btn-primary w-full"
            >
              {loading ? "Sending…" : "Send OTP"}
            </button>
          </div>
        ) : (
          <div className="space-y-6 pt-4">
            {otpHint && (
              <div className="bg-[hsl(var(--kb-surface-alt))] p-3 text-xs" data-testid="otp-hint">
                {otpHint}
              </div>
            )}
            <input
              data-testid="otp-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit OTP"
              maxLength={6}
              className="kb-input tracking-[0.5em] text-center text-2xl"
              autoFocus
            />
            <button
              data-testid="verify-otp-btn"
              disabled={loading}
              onClick={verify}
              className="kb-btn-primary w-full"
            >
              {loading ? "Verifying…" : "Verify & Sign in"}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button
                data-testid="change-phone-btn"
                className="text-[hsl(var(--kb-muted))] underline"
                onClick={() => setStep("phone")}
              >
                Change phone
              </button>
              <button
                data-testid="resend-otp-btn"
                disabled={resending}
                onClick={() => sendOtp(channel, true)}
                className="inline-flex items-center gap-1.5 text-[hsl(var(--kb-primary))] underline disabled:opacity-50"
              >
                <RefreshCw size={12} className={resending ? "animate-spin" : ""} />
                {resending ? "Resending…" : "Resend"}
              </button>
            </div>
            <div className="text-xs text-[hsl(var(--kb-muted))] text-center">
              Didn't receive it? Try the other channel:{" "}
              {CHANNELS.filter((c) => c.id !== channel).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setChannel(c.id); sendOtp(c.id, true); }}
                  className="underline text-[hsl(var(--kb-primary))]"
                  data-testid={`switch-channel-${c.id}`}
                >
                  Send via {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
